import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Quill from 'quill';
import 'quill/dist/quill.snow.css'; // Importa los estilos del editor
import dateFormat from 'date-fns/format';
import { toast } from 'react-hot-toast';

import { customersApiService, commentService } from '../../services';

function Tabs({ tabId, tabColors = { bgcolor: 'bg-orange-300', nocolor: 'bg-orange-100', hovercolor: 'hover:bg-orange-200' } }) {
  const { id } = useParams();
  const editorRef = useRef(null);
  const quillInstance = useRef(null);
  const [text, setText] = useState('');
  const [comments, setComments] = useState([]);
  const [, setTicket] = useState(null);
  const [quota, setQuota] = useState(null);

  // Extract border color from bgcolor (e.g., 'bg-orange-300' -> 'border-orange-300')
  const borderColor = tabColors.bgcolor.replace('bg-', 'border-');
  // Extract text color for inputs
  const textColorClass = `text-${tabColors.bgcolor.split('-').slice(1).join('-')}`;

  const removeColons = () => setText(text.replace(/:/g, ''));
  const removeNonDigits = () => setText(text.replace(/\D/g, ''));
  const addColonsEveryTwoChars = () => {
    setText(text.replace(/(.{2})/g, '$1:').slice(0, -1));
  };

  useEffect(() => {
    if (id) {
      customersApiService.getTicketById(id)
        .then(async data => {
          // Fetch fresh customer data to avoid stale cached names
          let customerName = data.customer_name;
          try {
            const customer = await customersApiService.getCustomerByNumber(data.customer_number);
            if (customer && customer.customer_name) {
              customerName = customer.customer_name;
            }
          } catch (err) {
            console.warn('Could not fetch fresh customer data, using cached name:', err);
          }

          setTicket(data);
          const pageTitle = `${data.ticket_number} - ${customerName} - ${data.issue_with}`;
          document.title = pageTitle;
        })
        .catch(err => {
          console.error('Error fetching ticket:', err);
          document.title = 'SSK - Support Swiss Knife';
        });
    } else {
      document.title = 'SSK - Support Swiss Knife';
    }

    return () => {
      document.title = 'SSK - Support Swiss Knife';
    };
  }, [id]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(text);
    alert('Text copied to clipboard!');
  };

  const fetchQuota = async () => {
    try {
      const quotaData = await commentService.getQuota(id);
      setQuota(quotaData);
    } catch (error) {
      console.error('Error fetching quota:', error);
    }
  };

  const handleDeleteComment = async commentId => {
    try {
      await commentService.deleteComment(commentId);
      setComments(comments.filter(comment => comment.id !== commentId));
      // Refresh quota after deletion
      await fetchQuota();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  useEffect(() => {
    if (!id) {
      console.error('ticket_id es undefined, revisa cómo se está pasando.');
      return;
    }

    const fetchComments = async () => {
      try {
        const data = await commentService.getCommentsForTab(id, tabId);
        console.log('Fetched comments:', data);
        setComments(data);
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    };
    fetchComments();

    // Fetch quota for customizable tabs (7, 8, 9)
    if ([7, 8, 9].includes(parseInt(tabId))) {
      fetchQuota();
    }
  }, [id, tabId]);

  const handleSaveComment = async () => {
    if (quillInstance.current) {
      const content = quillInstance.current.root.innerHTML;
      const tempElement = document.createElement('div');
      tempElement.innerHTML = content;

      try {
        await commentService.saveComment(tabId, content, id);

        const data = await commentService.getCommentsForTab(id, tabId);
        setComments(data);

        quillInstance.current.root.innerHTML = ''; // Clean the editor
        toast.success('Comment saved!', {
          style: {
            borderRadius: '10px',
            background: 'oklch(96.7% 0.003 264.542)',
            color: '#000',
            borderColor: 'oklch(57.7% 0.245 27.325)',
            borderWidth: '3px',
          },
          duration: 2000,
        });
      } catch (error) {
        toast.error('Error saving the comment.');
      }
    }
  };

  useEffect(() => {
    if (editorRef.current && !quillInstance.current) {
      quillInstance.current = new Quill(editorRef.current, {
        theme: 'snow', // "bubble" or "snow" themes
        placeholder: 'Text here...',
        modules: {
          toolbar: {
            container: [
              [{ header: [1, 2, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['link', 'image'],
              ['clean'],
            ],
            handlers: {
              image: () => selectLocalImage(),
            },
          },
        },
      });

      // Add paste handler for clipboard images
      // Use capture phase to handle event before Quill's handlers
      quillInstance.current.root.addEventListener('paste', handlePaste, true);
    }

    async function handlePaste(e) {
      const clipboardData = e.clipboardData || window.clipboardData;
      const items = clipboardData.items;

      if (!items) return;

      // Look for image in clipboard
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault(); // Prevent default paste behavior
          e.stopPropagation(); // Stop event from bubbling to prevent duplicate handling

          const blob = items[i].getAsFile();
          if (blob) {
            await uploadImageToServer(blob);
          }
          return; // Exit immediately after handling image paste
        }
      }
    }

    async function uploadImageToServer(file) {
      const formData = new FormData();
      formData.append('image', file);

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/comments/upload/image?ticket_id=${id}`,
          {
            method: 'POST',
            body: formData,
            credentials: 'include',
          }
        );

        if (!res.ok) {
          if (res.status === 413) {
            toast.error('Image is too large! (max. 5 MB)');
            return;
          }

          // Handle storage limit errors
          const errorData = await res.json();
          if (errorData.error === 'Image limit exceeded' || errorData.error === 'Storage limit exceeded') {
            toast.error(errorData.message, {
              duration: 5000,
            });
            return;
          }

          throw new Error('Error uploading image');
        }

        const data = await res.json();
        const range = quillInstance.current.getSelection(true);

        quillInstance.current.insertEmbed(range.index, 'image', data.url);

        // Refresh quota info
        await fetchQuota();

        // Show simple success message
        toast.success('Image uploaded!', {
          style: {
            borderRadius: '10px',
            background: 'oklch(96.7% 0.003 264.542)',
            color: '#000',
            borderColor: 'oklch(57.7% 0.245 27.325)',
            borderWidth: '3px',
          },
          duration: 2000,
        });
      } catch (err) {
        console.error('Error uploading image:', err);
        toast.error('Error uploading image');
      }
    }

    function selectLocalImage() {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();

      input.onchange = async () => {
        const file = input.files[0];
        if (file) {
          await uploadImageToServer(file);
        }
      };
    }

    return () => {
      // Cleanup paste event listener
      if (quillInstance.current) {
        quillInstance.current.root.removeEventListener('paste', handlePaste, true);
      }
    };
  }, []);

  return (
    <div className={`p-4 ${tabColors.nocolor} h-full w-full`}>
      {/* Quota information for customizable tabs */}
      {[7, 8, 9].includes(parseInt(tabId)) && quota && (
        <div className="mb-4 p-2 bg-red-50 border border-red-300 rounded">
          <p className="text-red-600 text-xs">
            <strong>Storage Quota:</strong> {quota.imageCount}/{quota.maxImages} images used | {quota.totalSizeMB}/{quota.maxStorageMB} MB used
            {quota.remaining && ` | Remaining: ${quota.remaining.images} images, ${quota.remaining.storageMB} MB`}
          </p>
        </div>
      )}
      <div className="h-auto grid grid-cols-2 py-4">
        {/*Quill Text Editor*/}
        <div className={`border-2 ${borderColor} p-4 rounded-lg shadow-lg bg-white flex flex-col`}>
          <h2 className="text-lg font-bold mb-2">Text Editor</h2>
          <div ref={editorRef} style={{ height: '300px' }} />

          <button
            onClick={handleSaveComment}
            className="px-4 py-2 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center self-end mt-2"
          >
            Save Comment
          </button>
        </div>

        {/*Text cleaner editor*/}
        <div>
          <div className="px-4">
            <div className="py-2">
              <h1 className="text-xl font-semibold">Format Phone Numbers</h1>
            </div>

            <div className="flex flex-wrap gap-2 pb-2">
              <button
                className="px-4 py-2 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center"
                onClick={removeNonDigits}
              >
                Keep only numbers
              </button>
            </div>

            <div className="flex py-2">
              <input
                placeholder="Insert text here..."
                value={text}
                onChange={e => setText(e.target.value)}
                type="text"
                className={`${textColorClass} font-semibold text-xl w-full px-3 py-2 border border-gray-300 rounded-md shadow-md`}
              />

              <button
                onClick={copyToClipboard}
                className="px-4 py-2 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  style={{ width: '1.5rem', height: '1.5rem' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                  />
                </svg>
                Copy to Clipboard
              </button>
            </div>
          </div>

          <div className={`px-4 border-t ${borderColor} mt-4`}>
            <div className="py-2">
              <h1 className="text-xl font-semibold">Format MAC Address</h1>
            </div>

            <div className="flex flex-wrap gap-2 pb-2">
              <button
                className="px-4 py-2 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center"
                onClick={removeColons}
              >
                Remove Colons
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center"
                onClick={addColonsEveryTwoChars}
              >
                Add Colons
              </button>
            </div>

            <div className="flex py-2">
              <input
                placeholder="Insert text here..."
                value={text}
                onChange={e => setText(e.target.value)}
                type="text"
                className={`${textColorClass} font-semibold text-xl w-full px-3 py-2 border border-gray-300 rounded-md shadow-md`}
              />

              <button
                onClick={copyToClipboard}
                className="px-4 py-2 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  style={{ width: '1.5rem', height: '1.5rem' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                  />
                </svg>
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Saved comments Section */}
      <div className={`p-4 ${tabColors.nocolor} h-full`}>
        <div className={`mt-4 border-t pt-4 ${borderColor}`}>
          <h2 className="text-lg font-bold">Saved Comments</h2>
          {Array.isArray(comments) && comments.length > 0 ? (
            comments.map(comment => {
              let formattedDate = '';

              if (comment.created_at) {
                const parsedDate = new Date(comment.created_at);
                if (!isNaN(parsedDate)) {
                  formattedDate = dateFormat(parsedDate, 'dd MM yyyy, HH:mm');
                }
              }

              return (
                <div
                  key={comment.id}
                  className="border p-3 my-2 bg-white shadow-md rounded-md w-3/5 mx-auto"
                >
                  <div className="flex justify-between items-center">
                    <strong className="text-gray-800">
                      {comment.name} {comment.lastName}:
                    </strong>
                    <div className="flex items-center space-x-5">
                      <span className="text-sm text-gray-500">
                        {formattedDate}
                      </span>
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-500 transition text-sm"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          className="size-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="mt-2 text-gray-700 text-sm">
                    <div
                      dangerouslySetInnerHTML={{ __html: comment.content }}
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <p>No comments available.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Tabs;
